import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import ProductCard from "@/components/product/ProductCard";
import { useApp } from "@/context/AppContext";
import { SlidersHorizontal, X } from "lucide-react";

export default function Shop() {
  const { slug } = useParams();
  const [sp, setSp] = useSearchParams();
  const { categories, collections } = useApp();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const q = sp.get("q") || "";
  const sub = sp.get("sub") || "";
  const sort = sp.get("sort") || "newest";
  const priceMin = sp.get("min") || "";
  const priceMax = sp.get("max") || "";
  const stockOnly = sp.get("stock") === "1";

  const info = useMemo(() => {
    if (!slug) return { title: "SHOP ALL", type: null, obj: null };
    const cat = categories.find((c) => c.slug === slug);
    if (cat) return { title: cat.name, type: "category", obj: cat };
    const col = collections.find((c) => c.slug === slug);
    if (col) return { title: col.name, type: "collection", obj: col };
    return { title: "SHOP", type: null, obj: null };
  }, [slug, categories, collections]);

  useEffect(() => {
    const params = { limit: 60 };
    params.sort = sort;
    if (q) params.q = q;
    if (info.type === "category") {
      params.category = info.obj.name;
      if (sub) params.subcategory = sub;
    } else if (info.type === "collection") {
      // Smart collections
      if (slug === "new-arrivals") params.sort = "newest";
      else if (slug === "best-sellers") params.tag = "best-seller";
      else if (slug === "deals") params.tag = "deal";
      else if (slug === "trending") params.tag = "trending";
      else if (slug === "premium-picks") params.tag = "premium";
      else if (slug === "car-care-essentials") params.category = "Car Care";
      else if (slug === "interior-upgrade") params.category = "Interior Accessories";
      else if (slug === "exterior-upgrade") params.category = "Exterior Accessories";
      else if (slug === "under-499") params.max_price = 499;
      else if (slug === "under-999") params.max_price = 999;
    }
    if (priceMin) params.min_price = Number(priceMin);
    if (priceMax) params.max_price = Number(priceMax);
    if (stockOnly) params.in_stock = true;

    setLoading(true);
    api.get("/products", { params }).then((r) => {
      setItems(r.data.items || []); setTotal(r.data.total || 0);
    }).finally(() => setLoading(false));
  }, [slug, q, sort, priceMin, priceMax, stockOnly, sub, info.type, info.obj]);

  const updateParam = (k, v) => {
    const n = new URLSearchParams(sp);
    if (v === "" || v === null || v === undefined) n.delete(k); else n.set(k, v);
    setSp(n, { replace: true });
  };

  return (
    <div data-testid="shop-page" className="max-w-[1400px] mx-auto px-4 md:px-8 py-8 md:py-14">
      <div className="mb-8 md:mb-12">
        <div className="overline text-white/50 mb-3">
          <Link to="/" className="hover:text-white">Home</Link> / <Link to="/shop" className="hover:text-white">Shop</Link>{info.title && <> / <span className="text-white">{info.title}</span></>}
        </div>
        <h1 className="font-display text-4xl md:text-6xl tracking-tighter">{q ? `SEARCH: "${q}"` : info.title}</h1>
        {info.obj?.description && <p className="mt-3 text-white/60 max-w-2xl">{info.obj.description}</p>}
      </div>

      <div className="grid md:grid-cols-[240px_1fr] gap-8">
        {/* Filters */}
        <aside className={`${filtersOpen ? "fixed inset-0 z-50 bg-black/90 p-6 overflow-y-auto" : "hidden md:block"}`}>
          <div className="md:hidden flex items-center justify-between mb-6">
            <div className="overline">Filters</div>
            <button onClick={() => setFiltersOpen(false)} className="p-2"><X size={20} /></button>
          </div>

          {info.type === "category" && info.obj?.subcategories?.length > 0 && (
            <div className="mb-8">
              <div className="overline text-white/60 mb-3">Subcategories</div>
              <div className="space-y-2">
                <button onClick={() => updateParam("sub","")} className={`block text-sm ${!sub ? "text-[#E63946]" : "text-white/80 hover:text-white"}`}>All</button>
                {info.obj.subcategories.map((s) => (
                  <button key={s} onClick={() => updateParam("sub", s)} className={`block text-sm text-left ${sub === s ? "text-[#E63946]" : "text-white/80 hover:text-white"}`}>{s}</button>
                ))}
              </div>
            </div>
          )}

          <div className="mb-8">
            <div className="overline text-white/60 mb-3">Price (₹)</div>
            <div className="flex gap-2">
              <input type="number" placeholder="Min" defaultValue={priceMin} onBlur={(e) => updateParam("min", e.target.value)} className="sa-input py-2 text-sm" />
              <input type="number" placeholder="Max" defaultValue={priceMax} onBlur={(e) => updateParam("max", e.target.value)} className="sa-input py-2 text-sm" />
            </div>
          </div>

          <div className="mb-8">
            <label className="flex items-center gap-3 text-sm cursor-pointer">
              <input type="checkbox" checked={stockOnly} onChange={(e) => updateParam("stock", e.target.checked ? "1" : "")} className="accent-[#E63946]" />
              In stock only
            </label>
          </div>
        </aside>

        {/* Product Grid */}
        <div>
          <div className="flex items-center justify-between mb-6">
            <div className="text-sm text-white/60">{loading ? "Loading..." : `${total} product${total === 1 ? "" : "s"}`}</div>
            <div className="flex items-center gap-3">
              <button onClick={() => setFiltersOpen(true)} className="md:hidden sa-btn sa-btn-outline py-2 px-3 text-xs" data-testid="filters-toggle">
                <SlidersHorizontal size={14} /> Filters
              </button>
              <select value={sort} onChange={(e) => updateParam("sort", e.target.value)} className="sa-input py-2 text-sm w-40" data-testid="sort-select">
                <option value="newest">Newest</option>
                <option value="price_asc">Price: Low → High</option>
                <option value="price_desc">Price: High → Low</option>
                <option value="rating">Top Rated</option>
                <option value="name">Name (A-Z)</option>
              </select>
            </div>
          </div>

          {items.length === 0 ? (
            <div className="border border-dashed border-white/15 py-16 px-6 text-center">
              <div className="font-display text-2xl mb-3">No products found</div>
              <p className="text-sm text-white/50 max-w-md mx-auto">Try adjusting your filters, or explore other categories. Admins can add products from the admin panel.</p>
              <Link to="/ask-expert" className="sa-btn sa-btn-primary mt-6 inline-flex">ASK AN EXPERT</Link>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6" data-testid="product-grid">
              {items.map((p) => <ProductCard key={p.id} product={p} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
